import { useEffect, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { useCurrentUser } from "../../../hooks/useCurrentUser"
import { getCsrfToken } from "../utils"
import { SignInModal } from "./SignInModal"
import { WalletGenerationModal } from "./WalletGenerationModal"
import type { LoyaltyProgram } from "../types"

interface LoyaltyCardClaimProps {
  loyaltyPrograms: LoyaltyProgram[]
  loyaltyCardGenerateUrl: string
  appleAuthUrl: string
  googleAuthUrl: string
  coffeeShopSlug: string
}

type ClaimState = "idle" | "auth_required"

export function LoyaltyCardClaim({
  loyaltyPrograms,
  loyaltyCardGenerateUrl,
  appleAuthUrl,
  googleAuthUrl,
  coffeeShopSlug,
}: LoyaltyCardClaimProps) {
  const { data: meData, isLoading: meLoading } = useCurrentUser()
  const current_user = meData?.current_user ?? null

  const [state, setState] = useState<ClaimState>("idle")
  const [claimPlatform, setClaimPlatform] = useState<string | null>(null)
  const [claimProgramUuid, setClaimProgramUuid] = useState<string | null>(null)
  const [pendingClaim, setPendingClaim] = useState(false)
  const [showGenerationModal, setShowGenerationModal] = useState(false)
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null)
  const [modalStatus, setModalStatus] = useState<
    "generating" | "completed" | "failed"
  >("generating")
  const [modalError, setModalError] = useState("")
  // Apple-specific polling data
  const [applePollingData, setApplePollingData] = useState<{
    loyaltyCardId: number
    generationStatusUrl: string
  } | null>(null)
  const processedRef = useRef(false)

  // Parse URL params on mount (once)
  useEffect(() => {
    if (typeof window === "undefined") return

    const params = new URLSearchParams(window.location.search)
    const claimingCard = params.get("claiming_card")
    const platform = params.get("platform")
    const programUuid = params.get("program_uuid")

    if (claimingCard !== "true" || !platform) return

    setClaimPlatform(platform)
    if (programUuid) setClaimProgramUuid(programUuid)
    setPendingClaim(true)

    // Clean up URL params so Back/refresh don't re-run the claim. Go through
    // Inertia: a raw replaceState is overwritten by its initial history write.
    const url = new URL(window.location.href)
    url.searchParams.delete("claiming_card")
    url.searchParams.delete("platform")
    url.searchParams.delete("program_uuid")
    router.replace({
      url: url.pathname + url.search + url.hash,
      preserveState: true,
      preserveScroll: true,
    })
  }, [])

  // Act on pending claim once /me has loaded
  useEffect(() => {
    if (!pendingClaim || meLoading || processedRef.current) return
    processedRef.current = true
    setPendingClaim(false)

    if (!current_user) {
      setState("auth_required")
      return
    }

    claimCard(claimPlatform!, claimProgramUuid || loyaltyPrograms[0]?.uuid)
  }, [pendingClaim, meLoading]) // eslint-disable-line react-hooks/exhaustive-deps

  async function claimCard(platform: string, programUuid?: string | null) {
    // Open modal immediately
    setModalStatus("generating")
    setModalError("")
    setRedirectUrl(null)
    setApplePollingData(null)
    setShowGenerationModal(true)

    const body: Record<string, string> = { platform }
    if (programUuid) body.program_uuid = programUuid

    try {
      const res = await fetch(loyaltyCardGenerateUrl, {
        method: "POST",
        headers: {
          "X-CSRF-Token": getCsrfToken(),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(body),
      })

      if (res.status === 401) {
        setShowGenerationModal(false)
        setState("auth_required")
        return
      }

      if (!res.ok) {
        const data = await res.json()
        setModalError(data.error || "Something went wrong. Please try again.")
        setModalStatus("failed")
        return
      }

      const data = await res.json()

      if (data.platform === "apple" && data.download_url) {
        // Set polling data — modal switches to polling mode
        setApplePollingData({
          loyaltyCardId: data.loyalty_card_id,
          generationStatusUrl: data.generation_status_url,
        })
        setRedirectUrl(data.download_url)
      } else if (data.platform === "google" && data.wallet_url) {
        setRedirectUrl(data.wallet_url)
        setModalStatus("completed")
      }
    } catch {
      setModalError("Network error. Please try again.")
      setModalStatus("failed")
    }
  }

  if (state === "auth_required") {
    const programParam = claimProgramUuid
      ? `&program_uuid=${claimProgramUuid}`
      : ""
    const claimUrl = `${window.location.origin}/${coffeeShopSlug}?claiming_card=true&platform=${claimPlatform}${programParam}`

    return (
      <SignInModal
        open
        onOpenChange={(open) => {
          if (!open) setState("idle")
        }}
        title="Sign In Required"
        description="Please sign in to get your loyalty card."
        appleAuthUrl={appleAuthUrl}
        googleAuthUrl={googleAuthUrl}
        origin={claimUrl}
      />
    )
  }

  return (
    <WalletGenerationModal
      open={showGenerationModal}
      onClose={() => {
        setShowGenerationModal(false)
        setApplePollingData(null)
        setRedirectUrl(null)
      }}
      onRetry={() => {
        if (claimPlatform) claimCard(claimPlatform, claimProgramUuid)
      }}
      redirectUrl={redirectUrl}
      loyaltyCardId={applePollingData?.loyaltyCardId}
      generationStatusUrl={applePollingData?.generationStatusUrl}
      status={modalStatus}
      errorMessage={modalError}
    />
  )
}
